import { displayDateUS } from "../../utils/dateUtils";
import { ORIENTATION_ITEMS } from "../../pages/Application/AdministrativeOrientation";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseAdministrativeOrientationFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.orientation) {
    return result.orientation;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.applicantName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/**
 * @param {unknown[]} apiItems
 */
function mergeOrientationItems(apiItems) {
  const raw = Array.isArray(apiItems) ? apiItems : [];
  const formatted = raw.map((item) => {
    if (!item || typeof item !== "object") return item;
    const rec = /** @type {{ dateDiscussed?: string }} */ (item);
    const d = rec.dateDiscussed;
    return {
      ...item,
      dateDiscussed: d && typeof d === "string" && d.includes("T") ? d.split("T")[0] : d,
    };
  });

  return ORIENTATION_ITEMS.map((defaultItem) => {
    const existing = formatted.find(
      (i) =>
        i &&
        typeof i === "object" &&
        /** @type {{ itemName?: string; section?: string }} */ (i)
          .itemName === defaultItem.itemName &&
        /** @type {{ section?: string }} */ (i).section === defaultItem.section,
    );
    return existing
      ? { ...defaultItem, ...existing }
      : {
          ...defaultItem,
          isChecked: false,
          employeeInitials: "",
          dateDiscussed: "",
        };
  });
}

function isCheckedVal(v) {
  if (v === true) return true;
  if (v === false || v == null) return false;
  const s = String(v).toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildAdministrativeOrientationPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const applicantName = escapeHtml(
    String(record.applicantName || record.applicantSignedBy || "").trim(),
  );
  const position = escapeHtml(String(record.position || ""));

  const mergedItems = mergeOrientationItems(
    /** @type {unknown[]} */ (record.items),
  );
  const sections = Array.from(
    new Set(ORIENTATION_ITEMS.map((item) => item.section)),
  );

  const tablesHtml = sections
    .map((section) => {
      const rows = mergedItems
        .filter((row) => row.section === section)
        .map((row) => {
          const chk = isCheckedVal(row.isChecked) ? "☑" : "☐";
          const initials = escapeHtml(String(row.employeeInitials || ""));
          const d = escapeHtml(formatDate(row.dateDiscussed));
          return `<tr>
            <td class="ao-td ao-item">${escapeHtml(row.itemName || "")}</td>
            <td class="ao-td ao-c">${chk}</td>
            <td class="ao-td ao-c">${initials || "—"}</td>
            <td class="ao-td ao-c">${d || "—"}</td>
          </tr>`;
        })
        .join("");
      return `<section class="ao-sec">
        <h2 class="ao-sec-h">${escapeHtml(section)}</h2>
        <table class="ao-table">
          <thead>
            <tr>
              <th class="ao-th">Item</th>
              <th class="ao-th ao-c">Acknowledged</th>
              <th class="ao-th ao-c" style="width:88px">Initials</th>
              <th class="ao-th ao-c" style="width:100px">Date discussed</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
    })
    .join("");

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature || record.empSignature || nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || ""),
  );
  const reviewedBySig = record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!record.applicantStatementAccepted;
  const statusCompleted =
    String(record.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Administrative Orientation — ${applicantName || "Applicant"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.4;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 900px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 28px 32px 36px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 14px;
    }
    .logo { height: 52px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 12px; font-weight: 500; color: #2563eb; margin: 0; }
    .ao-meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 16px;
    }
    .ao-meta-lbl { font-size: 9px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
    .ao-meta-val {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 11px;
      min-height: 34px;
    }
    .ao-sec { margin-bottom: 18px; page-break-inside: auto; }
    .ao-sec-h {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-bottom: none;
      margin: 0;
      padding: 10px 12px;
      border-radius: 8px 8px 0 0;
      color: #0f172a;
    }
    .ao-table { width: 100%; border-collapse: collapse; font-size: 10px; border: 1px solid #e2e8f0; border-radius: 0 0 8px 8px; overflow: hidden; }
    .ao-th { text-align: left; padding: 8px 10px; background: #fff; border-bottom: 1px solid #e5e7eb; font-weight: 700; color: #374151; }
    .ao-th.ao-c, .ao-c { text-align: center; }
    .ao-td { padding: 8px 10px; border-top: 1px solid #f1f5f9; vertical-align: middle; }
    .ao-item { font-weight: 500; color: #1e293b; }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-top: 18px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 12px; font-weight: 700; margin: 0 0 10px 0; color: #111827; }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 26px;
      font-size: 11px;
      padding: 4px 0;
    }
    .sig-img { max-height: 44px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 18px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 9px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Administrative orientation checklist</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="ao-meta">
      <div>
        <div class="ao-meta-lbl">Applicant name</div>
        <div class="ao-meta-val">${applicantName || "—"}</div>
      </div>
      <div>
        <div class="ao-meta-lbl">Position</div>
        <div class="ao-meta-val">${position || "—"}</div>
      </div>
    </div>

    ${tablesHtml}

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="ao-meta-val" style="margin-bottom:8px;">${applicantName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="ao-meta-val" style="margin-bottom:8px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
